import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { PHOTOS } from "@/lib/images";
export default function AboutMe() {
  const { t, lang } = useT();
  const a = t.about;

  return (
    <section
      id="sobre"
      data-testid="about-me-section"
      className="relative py-24 md:py-36 bg-[#F7F3F7]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-9">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F] mb-6">
              {a.eyebrow}
            </p>
            <h2
              data-testid="about-headline"
              className="font-serif font-light text-[clamp(2.4rem,5.6vw,5.5rem)] leading-[0.98] tracking-tight"
            >
              {a.headline}
            </h2>
          </div>
        </div>

        {/* Portrait + intro paragraphs */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-12 md:col-span-5 relative md:sticky md:top-28"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={PHOTOS.aboutPortrait}
                alt={a.caption}
                className="w-full h-full object-cover"
                data-testid="about-portrait"
              />
            </div>
            <div className="hidden md:block absolute -bottom-6 -right-6 w-32 h-32 border vk-line-strong" />
            <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-[#6B4E6F]">
              {a.caption}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="col-span-12 md:col-span-7 space-y-6 text-lg leading-relaxed text-[#4B2F4F]/85"
            data-testid="about-body"
          >
            {a.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </motion.div>
        </div>

        {/* Transformation visual — before/after */}
        <motion.figure
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mt-20 md:mt-28 relative"
          data-testid="about-transformation"
        >
          <div className="relative overflow-hidden">
            <img
              src={PHOTOS.transformation}
              alt={
                lang === "en"
                  ? "Before: working in hospitality. After: managing operations remotely."
                  : "Antes: trabajando en hostelería. Después: gestionando operaciones de forma remota."
              }
              loading="lazy"
              className="w-full h-auto object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-[#4B2F4F]/10 pointer-events-none" />
          </div>

          <div className="mt-6 grid grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#E0B25B]">
                {a.beforeLabel}
              </p>
              <p className="mt-2 font-serif text-lg md:text-xl text-[#4B2F4F] leading-snug">
                {a.beforeText}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#E0B25B]">
                {a.afterLabel}
              </p>
              <p className="mt-2 font-serif text-lg md:text-xl text-[#4B2F4F] leading-snug">
                {a.afterText}
              </p>
            </div>
          </div>
        </motion.figure>

        {/* Closing pull quote + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 md:mt-28 grid grid-cols-12 gap-6 md:gap-12"
        >
          <div className="col-span-12 md:col-span-10 md:col-start-2 border-l-2 border-[#E0B25B] pl-8 md:pl-12">
            <p
              data-testid="about-closing"
              className="vk-pull-quote text-3xl md:text-5xl leading-[1.08]"
            >
              {a.closing}
            </p>
          </div>
        </motion.div>

        <div className="mt-14 flex justify-center">
          <a
            href="#contacto"
            data-testid="about-cta"
            className="group inline-flex items-center gap-3 bg-[#4B2F4F] text-[#F7F3F7] px-10 py-5 text-[12px] uppercase tracking-[0.24em] font-medium hover:bg-[#E0B25B] transition-colors duration-300"
          >
            {a.cta}
            <ArrowRight
              size={16}
              strokeWidth={1.4}
              className="group-hover:translate-x-1 transition-transform duration-300"
            />
          </a>
        </div>
      </div>
    </section>
  );
}
